<template>
  <details
    :id="computedId"
    class="c-details"
    :name="groupName"
    :aria-labelledby="titleId"
    @toggle="handleToggle"
  >
    <summary class="c-details__label" ref="summaryElement">
      <slot name="summary"></slot>
    </summary>

    <div class="c-details__content">
      <slot></slot>
    </div>
  </details>
</template>

<script setup>
import { computed, inject, onMounted, ref, nextTick } from 'vue';
import { syncUrlHash } from '@shared/utils/algoliaFilters';

const props = defineProps({
  /**
   * Unique identifier for the accordion
   * If not provided, one will be generated
   */
  id: {
    type: String,
    default: null
  }
});

// Inject group configuration if available
const accordionGroup = inject('accordionGroup', null);

// Template refs
const summaryElement = ref(null);

// Generate unique ID if not provided
const uniqueId = ref(null);
const computedId = computed(() => props.id || uniqueId.value);
const groupName = computed(() => accordionGroup?.name);

// Accessibility enhancement: Reference title in summary
const titleId = ref(null);

onMounted(() => {
  if (!props.id) {
    // Generate group-scoped ID to prevent conflicts across multiple accordion groups
    const groupPrefix = accordionGroup?.name ? `${accordionGroup.name}-` : '';
    const randomSuffix = Math.random().toString(36).substr(2, 9);
    uniqueId.value = `${groupPrefix}accordion-${randomSuffix}`;
  }

  // After DOM is ready, find title in summary for aria-labelledby
  nextTick(() => {
    findAndLinkTitle();
  });
});

/**
 * Accessibility Enhancement: Link details to title in summary
 *
 * Addresses screen reader navigation where users skip from summary titles
 * directly to content headings, missing the accordion context.
 *
 * Benefits:
 * - Makes summary titles discoverable in heading navigation
 * - Cleaner scanning experience (title vs full summary content)
 * - Maintains access to full summary when needed
 * - Creates clear programmatic relationship
 */
const findAndLinkTitle = () => {
  if (!summaryElement.value) return;

  // Find the title heading (h1-h6) in the summary
  // Expected: single heading per summary for semantic clarity
  const title = summaryElement.value.querySelector('h1, h2, h3, h4, h5, h6');

  if (title) {
    // Generate group-scoped, semantic ID if title doesn't have one
    if (!title.id) {
      const groupPrefix = accordionGroup?.name ? `${accordionGroup.name}-` : '';
      // Use existing accordion ID or create group-scoped title ID
      if (props.id || uniqueId.value) {
        title.id = `${computedId.value}-title`;
      } else {
        title.id = `${groupPrefix}title-${Math.random().toString(36).substr(2, 9)}`;
      }
    }

    // Link details to the title via aria-labelledby
    titleId.value = title.id;
  }
  // Note: If no title found, summary content reads normally (still accessible)
};

/**
 * Handle accordion toggle events for URL hash synchronization
 *
 * Ensures URL hash stays in sync with actually opened accordion sections
 * Uses shared utility for consistent behavior across the application
 */
const handleToggle = (event) => {
  const isOpening = event.target.open;

  if (isOpening) {
    // When opening this accordion, sync URL hash
    // This will clear hash if it doesn't match this accordion's ID
    syncUrlHash(computedId.value);
  }
};
</script>